import type { SiteDocument } from '../../api/documents'
import { machines } from '../../content/machines'
import type { Project } from '../../content/projects.seed'
import type { Locale } from '../../content/site'
import { site } from '../../content/site'
import { blank, line, link, span, text, wrap } from './format'
import { markdownLines } from './markdown'
import { t } from './messages'
import type { Line, Span } from './types'

/** Only https URLs ever reach an anchor or `window.open`. */
export const safeUrl = (value: string): string | undefined => {
  try {
    return new URL(value).protocol === 'https:' ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * `owner/repo` for the first GitHub link on a project.
 *
 * Matched by URL host rather than by link label: the seed data labels links
 * `GitHub`, the CRM writes `repo`, and the Telegram bot writes `repo` too, so
 * a label match silently loses the preview for half the catalogue.
 */
export const githubRepo = (project: Project): string | undefined => {
  for (const item of project.links) {
    try {
      const url = new URL(item.url)
      if (url.protocol !== 'https:' || url.hostname !== 'github.com') continue
      const parts = url.pathname.split('/').filter(Boolean)
      if (parts.length >= 2) return `${parts[0]}/${parts[1]}`
    } catch {
      continue
    }
  }
  return undefined
}

/** Hand-picked previews that beat the generated Open Graph card. */
const PREVIEW_OVERRIDES: Record<string, string> = {
  'mk-droidscreencast':
    'https://raw.githubusercontent.com/MKultra6969/MK_DroidScreenCast/main/src-tauri/icons/icon.png',
  'mk-xraychecker':
    'https://raw.githubusercontent.com/MKultra6969/MK_XRAYchecker/main/menuShowcase/1.3.0.png',
}

export const projectPreview = (project: Project): string | undefined => {
  const uploaded = project.media[0]?.variants.webp.url
  if (uploaded) return uploaded
  const override = PREVIEW_OVERRIDES[project.slug]
  if (override) return override
  const repo = githubRepo(project)
  return repo ? `https://opengraph.githubassets.com/1/${repo}` : undefined
}

/** Repo stats, or nothing for a project that has no public repository. */
const stars = (project: Project): Line[] => {
  const { language, stars, forks, snapshotDate } = project.github
  const repo = stars > 0 || forks > 0 || Boolean(snapshotDate)
  if (!repo && !language) return []
  return [
    line(
      ...(repo
        ? [
            span('  ★ ', 'warn'),
            span(String(stars), 'num'),
            span('   forks ', 'dim'),
            span(String(forks), 'num'),
            span('   ', 'dim'),
          ]
        : [span('  ')]),
      span(language || 'unknown', 'accent'),
      ...(snapshotDate ? [span(`   snapshot ${snapshotDate}`, 'dim')] : []),
    ),
  ]
}

/** The full project card, shared by `projects <slug>` and `cat projects/<slug>.md`. */
export const projectLines = (project: Project, locale: Locale): Line[] => {
  const preview = projectPreview(project)
  const alt = project.media[0]?.alt[locale] ?? project.title[locale]
  return [
    line(
      span('# ', 'accent'),
      span(project.title[locale], 'key'),
      ...(project.year ? [span(`  (${project.year})`, 'dim')] : []),
    ),
    blank(),
    ...wrap(project.description[locale]).map((row) => text(row)),
    blank(),
    line(span(`${t('stack', locale)}: `, 'dim'), span(project.technologies.join(' · '), 'exec')),
    ...stars(project),
    ...(preview ? [{ ...line(span(`${t('preview', locale)}:`, 'dim')), media: { src: preview, alt } }] : []),
    ...project.links.flatMap((item) => {
      const url = safeUrl(item.url)
      return [
        line(span(`${item.label}: `, 'dim'), url ? link(url, url) : span(item.url, 'err')),
      ]
    }),
  ]
}

/** One row per project, aligned into a table. */
export const projectTable = (projects: Project[], locale: Locale): Line[] => {
  if (!projects.length) return [text(t('projectsEmpty', locale), 'dim')]
  const slug = Math.max(...projects.map((project) => project.slug.length))
  const language = Math.max(...projects.map((project) => project.github.language.length))
  return projects.map((project) =>
    line(
      span(project.slug.padEnd(slug + 2), 'dir'),
      span(project.github.language.padEnd(language + 2), 'accent'),
      span('★', 'warn'),
      span(String(project.github.stars).padStart(4), 'num'),
      span('  '),
      span(project.summary[locale]),
    ),
  )
}

/**
 * Renders one CRM-managed text file, or nothing when it has been deleted —
 * callers fall back to the generated listing rather than printing a blank.
 */
export const documentLines = (
  documents: readonly SiteDocument[],
  name: string,
  locale: Locale,
): Line[] | undefined => {
  const document = documents.find((item) => item.name === name)
  return document ? markdownLines(document.body[locale]) : undefined
}

export const linkLines = (): Line[] =>
  site.links.map((item, index) =>
    line(
      span(`link-${index + 1}`.padEnd(9), 'dim'),
      span(item.label.padEnd(20), 'key'),
      link(item.url, item.url),
    ),
  )

/** `work` when `~/work.txt` has been deleted in the CRM. */
export const workLines = (locale: Locale): Line[] => {
  const { work } = site
  return [
    line(span(work.company, 'key'), span(` — ${work.about[locale]}`)),
    blank(),
    line(span('role   ', 'dim'), span(work.role[locale])),
    line(span('since  ', 'dim'), span(String(work.since), 'num')),
    line(span('site   ', 'dim'), link(work.url, work.url)),
    blank(),
    text(work.nda[locale], 'warn'),
  ]
}

export const contactLines = (locale: Locale): Line[] => [
  ...site.links
    .filter((item) => item.url.startsWith('https://t.me/'))
    .map((item) => line(span(item.label.padEnd(20), 'key'), link(item.url, item.url))),
  blank(),
  text(t('contactHint', locale), 'dim'),
]

/**
 * Re-colours a captured fastfetch dump. The captures put ASCII art in a fixed
 * left column and `Key: value` rows on the right; the underline row on line 2
 * is the only reliable marker of where that column ends.
 */
export const machineLines = (slug: string): Line[] => {
  const machine = machines.find((item) => item.slug === slug)
  if (!machine) return []

  const rows = machine.fetch
  const gutter = Math.max(0, rows[1]?.indexOf('-') ?? 0)

  return rows.map((row, index) => {
    const art = row.slice(0, gutter)
    const info = row.slice(gutter)
    const spans: Span[] = art ? [span(art, art.trim() ? 'accent' : undefined)] : []

    if (!info) return { spans: spans.length ? spans : [{ text: '' }] }
    if (index === 0) {
      const at = info.indexOf('@')
      spans.push(
        at < 0 ? span(info, 'ok') : span(info.slice(0, at), 'ok'),
        ...(at < 0 ? [] : [span('@', 'dim'), span(info.slice(at + 1), 'ok')]),
      )
    } else if (index === 1) {
      spans.push(span(info, 'dim'))
    } else {
      const colon = info.indexOf(': ')
      if (colon < 0) spans.push(span(info))
      else spans.push(span(info.slice(0, colon + 1), 'key'), span(info.slice(colon + 1)))
    }
    return { spans }
  })
}

export const machineSlugs = machines.map((machine) => machine.slug)
